import React, { useState } from "react";
import API from "./api";
import { GoogleLogin } from "@react-oauth/google";

function Login({
  onLogin,
  embedded = false,
  onCancel = null,
  title = "Login to your account",
  subtitle = "Continue to Jain Kalyanam matrimonial workspace.",
  eyebrow = "Welcome back",
  showHeader = true,
  submitLabel = "Login",
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [pendingProvider, setPendingProvider] = useState("");
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [showRoleHint, setShowRoleHint] = useState(false);

  const saveSession = (data) => {
    setRoleOptions([]);
    setSelectedRoleId("");
    setPendingProvider("");
    setPendingGoogleCredential("");
    onLogin(data);
  };

  const clearRoleSelection = () => {
    setRoleOptions([]);
    setSelectedRoleId("");
    setPendingProvider("");
    setPendingGoogleCredential("");
    setShowRoleHint(false);
  };

  const awaitingRoleSelection = roleOptions.length > 0;

  const handleRoleSelectionResponse = (data, provider) => {
    if (!data?.requires_role_selection) {
      saveSession(data);
      return true;
    }

    const nextRoles = Array.isArray(data.roles) ? data.roles : [];
    setRoleOptions(nextRoles);
    setSelectedRoleId(String(nextRoles[0]?.id || ""));
    setPendingProvider(provider);
    return false;
  };

  const login = async (roleIdOverride = null) => {
    setError("");

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail || !password) {
      setError("Please enter email and password.");
      return;
    }

    setLoading(true);

    try {
      const res = await API.post("/auth/login", {
        email: cleanEmail,
        password,
        ...(roleIdOverride ? { role_id: Number(roleIdOverride) } : {}),
      });

      handleRoleSelectionResponse(res.data, "password");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Invalid email or password."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    setGoogleLoading(true);

    try {
      const credential = credentialResponse.credential;
      const res = await API.post("/auth/google", {
        credential,
      });

      if (!handleRoleSelectionResponse(res.data, "google")) {
        setPendingGoogleCredential(credential);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Google login failed."
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in was cancelled or failed. Please try again.");
  };

  const continueRoleSelection = async () => {
    setError("");

    if (!selectedRoleId) {
      setError("Please select a role to continue.");
      return;
    }

    if (pendingProvider === "google") {
      if (!pendingGoogleCredential) {
        setError("Google session expired. Please sign in again.");
        clearRoleSelection();
        return;
      }

      setGoogleLoading(true);

      try {
        const res = await API.post("/auth/google", {
          credential: pendingGoogleCredential,
          role_id: Number(selectedRoleId),
        });
        saveSession(res.data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Google login failed."
        );
      } finally {
        setGoogleLoading(false);
      }

      return;
    }

    await login(selectedRoleId);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading && !googleLoading) {
      if (awaitingRoleSelection) {
        continueRoleSelection();
        return;
      }
      login();
    }
  };

  const cardContent = (
    <div
      style={{
        ...styles.card,
        ...(embedded ? styles.cardEmbedded : {}),
        ...(darkMode && !embedded ? styles.cardDark : {}),
      }}
    >
      {!embedded && (
        <button
          type="button"
          style={{ ...styles.themeToggle, ...(darkMode ? styles.themeToggleDark : {}) }}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          onClick={() => setDarkMode((value) => !value)}
        >
          {darkMode ? <MoonIcon /> : <SunIcon />}
        </button>
      )}

      {!embedded && (
        <div style={styles.brandBlock}>
          <RingsIcon />
          <h1 style={{ ...styles.brandTitle, ...(darkMode ? styles.textDark : {}) }}>Jain Kalyanam</h1>
          <p style={{ ...styles.brandSubtitle, ...(darkMode ? styles.mutedDark : {}) }}>Matrimony Console</p>
        </div>
      )}

      {showHeader && (
        <div style={styles.cardHeader}>
          <p style={styles.cardEyebrow}>{eyebrow}</p>
          <div style={styles.titleRow}>
            <h2 style={{ ...styles.title, ...(darkMode ? styles.textDark : {}), margin: 0 }}>{title}</h2>
            {awaitingRoleSelection && (
              <span style={styles.roleHintWrap}>
                <button
                  type="button"
                  style={styles.roleHintBtn}
                  onClick={() => setShowRoleHint((value) => !value)}
                  aria-label="About role selection"
                  aria-expanded={showRoleHint}
                >
                  <InfoIcon />
                </button>
                {showRoleHint && (
                  <div style={{ ...styles.roleHintPopup, ...(darkMode ? styles.roleHintPopupDark : {}) }}>
                    This account has multiple roles. Choose one to continue with role-specific access.
                  </div>
                )}
              </span>
            )}
          </div>
          {!awaitingRoleSelection && (
            <p style={{ ...styles.subtitle, ...(darkMode ? styles.mutedDark : {}) }}>{subtitle}</p>
          )}
        </div>
      )}

      {error && <div style={styles.errorBox}>{error}</div>}

      {awaitingRoleSelection ? (
        <>
          <div style={styles.roleSelectBanner}>
            <strong style={styles.roleSelectTitle}>Choose active role</strong>
            <span style={styles.roleSelectText}>
              Your session will use only this role&apos;s permissions and policies.
            </span>
          </div>

          <div style={styles.roleOptionList}>
            {roleOptions.map((role) => {
              const isSelected = String(role.id) === String(selectedRoleId);

              return (
                <button
                  key={role.id}
                  type="button"
                  style={{
                    ...styles.roleOptionButton,
                    ...(isSelected ? styles.roleOptionButtonActive : {}),
                  }}
                  onClick={() => setSelectedRoleId(String(role.id))}
                >
                  <span style={styles.roleOptionName}>
                    {role.name || `Role #${role.id}`}
                  </span>
                  <span style={styles.roleOptionDesc}>
                    {role.description || "Use this role for the current login session."}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            style={{
              ...styles.button,
              ...((loading || googleLoading) ? styles.buttonDisabled : {}),
            }}
            onClick={continueRoleSelection}
            disabled={loading || googleLoading}
          >
            {loading || googleLoading ? "Continuing..." : "Continue with Selected Role"}
          </button>

          <button
            type="button"
            style={styles.cancelBtn}
            onClick={() => {
              clearRoleSelection();
              setError("");
            }}
            disabled={loading || googleLoading}
          >
            Start Over
          </button>
        </>
      ) : (
        <>
          <label style={{ ...styles.label, ...(darkMode ? styles.textDark : {}) }}>Email</label>
          <input
            style={{ ...styles.input, ...(darkMode ? styles.inputDark : {}) }}
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              if (pendingProvider) clearRoleSelection();
              setEmail(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            autoComplete="email"
          />

          <label style={{ ...styles.label, ...(darkMode ? styles.textDark : {}) }}>Password</label>
          <div style={styles.passwordFieldWrap}>
            <input
              type={showPassword ? "text" : "password"}
              style={{ ...styles.passwordInput, ...(darkMode ? styles.inputDark : {}) }}
              placeholder="Enter password"
              value={password}
              onChange={(e) => {
                if (pendingProvider) clearRoleSelection();
                setPassword(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              autoComplete="current-password"
            />
            <button
              type="button"
              style={styles.passwordToggleBtn}
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeIcon /> : <EyeOffIcon />}
            </button>
          </div>

          <button
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            onClick={() => login()}
            disabled={loading}
          >
            {loading ? "Logging in..." : submitLabel}
          </button>

          <div style={styles.divider}>
            <span style={{ ...styles.dividerLine, ...(darkMode ? styles.dividerLineDark : {}) }} />
            <span style={{ ...styles.dividerText, ...(darkMode ? styles.mutedDark : {}) }}>or continue with</span>
            <span style={{ ...styles.dividerLine, ...(darkMode ? styles.dividerLineDark : {}) }} />
          </div>

          <div style={styles.googleWrapper}>
            {googleLoading ? (
              <div style={styles.googleLoading}>Connecting Google...</div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                text="continue_with"
                shape="pill"
                theme="outline"
                width="330"
              />
            )}
          </div>
        </>
      )}

      <p style={{ ...styles.securityNote, ...(darkMode ? styles.mutedDark : {}) }}>
        Access is restricted to approved team members.
      </p>

      {embedded && onCancel && (
        <button type="button" style={styles.cancelBtn} onClick={onCancel}>
          Cancel
        </button>
      )}
    </div>
  );

  if (embedded) {
    return <div style={styles.embeddedContainer}>{cardContent}</div>;
  }

  return (
    <div style={{ ...styles.container, ...(darkMode ? styles.containerDark : {}) }}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={styles.shell}>{cardContent}</div>
    </div>
  );
}

function RingsIcon() {
  return (
    <svg width="26" height="18" viewBox="0 0 36 24" fill="none" aria-hidden="true">
      <circle cx="14" cy="12" r="10" fill="none" stroke="#e0344a" strokeWidth="2.6" />
      <circle cx="22" cy="12" r="10" fill="none" stroke="#3b6fe0" strokeWidth="2.6" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20.5 14.5A8.5 8.5 0 1 1 9.5 3.5a7 7 0 0 0 11 11Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 2v2.5M12 19.5V22M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2 12h2.5M19.5 12H22M4.2 19.8 6 18M18 6l1.8-1.8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.7" />
      <rect x="11.1" y="10.1" width="1.8" height="6.6" rx="0.9" fill="currentColor" />
      <rect x="11.1" y="6.4" width="1.8" height="1.9" rx="0.9" fill="currentColor" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 3l18 18M10.6 5.2A10.6 10.6 0 0 1 12 5c7 0 10.5 7 10.5 7a13.4 13.4 0 0 1-3.1 4.1M6.7 6.7C3.6 8.6 1.5 12 1.5 12s3.5 7 10.5 7c1.4 0 2.7-.28 3.9-.77M9.9 9.9a3 3 0 0 0 4.24 4.24"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Login;

const styles = {
  embeddedContainer: {
    width: "100%",
    margin: "0 auto",
  },
  container: {
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
    padding: "28px 18px",
    background: "#f2f3f5",
    fontFamily: "var(--jm-font-body)",
    boxSizing: "border-box",
    transition: "background 0.2s ease",
  },
  containerDark: {
    background: "#101114",
  },

  themeToggle: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 30,
    height: 30,
    borderRadius: 9,
    display: "grid",
    placeItems: "center",
    background: "#fff",
    border: "1px solid rgba(20,20,30,0.10)",
    color: "#5b5b66",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(20,20,30,0.06)",
    zIndex: 2,
  },
  themeToggleDark: {
    background: "#1c1d22",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#c7c7d1",
    boxShadow: "none",
  },

  shell: {
    position: "relative",
    zIndex: 1,
    width: "min(400px, 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    animation: "fadeUp 0.38s ease both",
  },

  card: {
    position: "relative",
    width: "100%",
    padding: "26px 28px",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 8px 30px rgba(20,20,30,0.08)",
    textAlign: "left",
    border: "1px solid rgba(20,20,30,0.06)",
    boxSizing: "border-box",
    transition: "background 0.2s ease, border-color 0.2s ease",
  },
  cardDark: {
    background: "#1a1b20",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
  },
  cardEmbedded: {
    width: "100%",
    margin: 0,
    padding: 0,
    background: "transparent",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    backdropFilter: "none",
  },

  brandBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 18,
  },

  brandTitle: {
    margin: "8px 0 0",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 17,
    fontWeight: 700,
    color: "#1a1a22",
    textAlign: "center",
  },

  brandSubtitle: {
    margin: "2px 0 0",
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: "#8a8a96",
    textAlign: "center",
  },

  textDark: {
    color: "#f2f2f5",
  },
  mutedDark: {
    color: "#8b8c96",
  },
  inputDark: {
    background: "#101114",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "#f2f2f5",
  },
  dividerLineDark: {
    background: "rgba(255,255,255,0.12)",
  },

  cardHeader: {
    marginBottom: 18,
    textAlign: "center",
  },

  cardEyebrow: {
    margin: 0,
    color: "#e0344a",
    fontSize: 10.5,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1.6,
  },

  titleRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    marginTop: 6,
  },

  roleHintWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
  },

  roleHintBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    padding: 0,
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: "#8a8a96",
    cursor: "pointer",
    lineHeight: 1,
  },

  roleHintPopup: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: "50%",
    transform: "translateX(-50%)",
    width: 240,
    padding: "10px 12px",
    borderRadius: 10,
    background: "#1a1a22",
    color: "#f2f2f5",
    fontSize: 11.5,
    lineHeight: 1.5,
    fontWeight: 500,
    textAlign: "center",
    boxShadow: "0 10px 24px rgba(20,20,30,0.24)",
    zIndex: 3,
  },
  roleHintPopupDark: {
    background: "#2a2b33",
    boxShadow: "0 10px 24px rgba(0,0,0,0.4)",
  },

  title: {
    margin: "6px 0 0",
    color: "#15151d",
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.25,
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#8a8a96",
    fontSize: 12.5,
    lineHeight: 1.5,
  },

  errorBox: {
    background: "#fff4f4",
    border: "1px solid rgba(224,52,74,0.22)",
    color: "#c22a3f",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 14,
    fontSize: 12.5,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  label: {
    display: "block",
    marginBottom: 6,
    color: "#3b3b45",
    fontSize: 12,
    fontWeight: 600,
  },

  input: {
    width: "100%",
    height: 38,
    padding: "0 12px",
    marginBottom: 14,
    borderRadius: 9,
    border: "1px solid rgba(20,20,30,0.14)",
    background: "#fff",
    color: "#15151d",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    transition: "background 0.2s ease, border-color 0.2s ease, color 0.2s ease",
  },

  passwordFieldWrap: {
    position: "relative",
    marginBottom: 14,
  },

  passwordInput: {
    width: "100%",
    height: 38,
    padding: "0 40px 0 12px",
    marginBottom: 0,
    borderRadius: 9,
    border: "1px solid rgba(20,20,30,0.14)",
    background: "#fff",
    color: "#15151d",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    transition: "background 0.2s ease, border-color 0.2s ease, color 0.2s ease",
  },

  passwordToggleBtn: {
    position: "absolute",
    top: 0,
    right: 0,
    height: 38,
    width: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    color: "#8a8a96",
    cursor: "pointer",
  },

  button: {
    width: "100%",
    height: 40,
    background: "#e0344a",
    color: "#fff",
    border: "none",
    borderRadius: 9,
    cursor: "pointer",
    fontWeight: 700,
    letterSpacing: 0.2,
    marginTop: 4,
    marginBottom: 18,
    fontSize: 13,
    boxShadow: "0 10px 22px rgba(224,52,74,0.24)",
  },

  buttonDisabled: {
    opacity: 0.62,
    cursor: "not-allowed",
  },

  divider: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },

  dividerLine: {
    flex: 1,
    height: 1,
    background: "rgba(20,20,30,0.12)",
    display: "block",
  },

  dividerText: {
    color: "#8a8a96",
    fontSize: 12,
    fontWeight: 700,
  },

  googleWrapper: {
    display: "flex",
    justifyContent: "center",
    minHeight: 42,
  },

  googleLoading: {
    width: "100%",
    height: 42,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: "#f7f7f9",
    color: "#8a8a96",
    fontSize: 13,
    fontWeight: 700,
    border: "1px solid rgba(20,20,30,0.12)",
  },
  roleSelectBanner: {
    display: "grid",
    gap: 4,
    padding: "14px 16px",
    borderRadius: 14,
    background: "#fff7f4",
    border: "1px solid rgba(224,52,74,0.14)",
    marginBottom: 14,
  },
  roleSelectTitle: {
    fontSize: 14,
    color: "#e0344a",
  },
  roleSelectText: {
    fontSize: 12,
    color: "#8a8a96",
    lineHeight: 1.5,
  },
  roleOptionList: {
    display: "grid",
    gap: 10,
    marginBottom: 14,
  },
  roleOptionButton: {
    border: "1px solid rgba(20,20,30,0.12)",
    background: "#fff",
    borderRadius: 14,
    padding: "13px 14px",
    display: "grid",
    gap: 4,
    textAlign: "left",
    cursor: "pointer",
  },
  roleOptionButtonActive: {
    background: "#fff4f0",
    borderColor: "rgba(224,52,74,0.28)",
    boxShadow: "0 10px 22px rgba(224,52,74,0.10)",
  },
  roleOptionName: {
    fontSize: 14,
    fontWeight: 800,
    color: "#15151d",
  },
  roleOptionDesc: {
    fontSize: 12,
    color: "#8a8a96",
    lineHeight: 1.45,
  },

  securityNote: {
    margin: "14px 0 0",
    textAlign: "center",
    color: "#8a8a96",
    fontSize: 11,
    fontWeight: 600,
  },
  cancelBtn: {
    width: "100%",
    marginTop: 12,
    height: 44,
    borderRadius: 10,
    border: "1px solid rgba(20,20,30,0.14)",
    background: "#fff",
    color: "#5b5b66",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
};
